import { CONFIGS, useConfigFilters, useConfigQuery } from '@buildwise/libs/configurations';
import { useBuildWiseModules } from '@buildwise/libs/helpers';
import useAddPostForm from '@buildwise/pages/addPost/hooks/useAddPostForm';
import {
  ArticleSourceType,
  ArticleFieldsType,
  ArticleType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import useProjectDetailsQuery from '@buildwise/pages/project/hooks/useProjectDetailsQuery';
import { Divider, Box, IconButton, Paper, Typography, Grid, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { theme } from 'config/theme';
import useUserData from 'hooks/useUserData';
import { useRef, useEffect, useMemo, useState, useCallback } from 'react';
import { FormProvider } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import ArrowLeftLineIcon from 'remixicon-react/ArrowLeftLineIcon';
import BuildingLineIcon from 'remixicon-react/BuildingLineIcon';
import { useProjectRetroContext } from '../../context';
import AddLessonForm from './AddLessonForm';
import { ReactComponent as RetrospectiveFillIcon } from 'assets/icons/RetrospectiveFillIcon.svg';
import RetrosHeader from './RetrosHeader';
import LessonsTable from './LessonsTable';
import RetroSummarizedResults from './RetroSummarizedResults';
import ViewLearning from './ViewLearning';
import { BUILDWISE_MODULES_FEATURES } from 'components/drawer/constants';
import ArticleBox from '@buildwise/pages/knowledge/components/ArticleBox';
import { getArticleFormDefaultValues } from '@buildwise/libs/posts';
import PhasesDrawer from './PhasesDrawer';
import Filter from 'components/common/filter/Filter';
import AddLineIcon from 'remixicon-react/AddLineIcon';
import { useRetrosFilters } from '../../hooks';
import AlertModal from 'components/utils/AlertModal';

function Retrospectives() {
  const {
    state: {
      isFullScreen,
      isOpenedLessonForm,
      selectedArticle,
      view,
      selectedLessonType,
      articleIds,
    },
    dispatch,
  } = useProjectRetroContext();
  const { companyId, isNovaAdmin } = useUserData();
  const { activeUniqueEntries: phases } = useConfigQuery({
    companyId,
    configName: CONFIGS.PROJECT_PHASES.configName,
  });
  const { projectId = '' } = useParams();
  const { project, refetch, projectArticles, isLoading } = useProjectDetailsQuery({
    projectId,
    shouldFetch: false,
  });
  const {
    articleFields,
    isLoadingArticleConfigFields,
    loadingFiles,
    postForm,
    openNovaAdminConfirmation,
    discardLoading,
    setOpenNovaAdminConfirmation,
    onDrop,
    onSubmit,
  } = useAddPostForm({
    setOpen: () => {
      dispatch({
        payload: {
          isOpenedLessonForm: false,
        },
      });
    },
    articleType: ArticleSourceType.LESSON,
    articleFieldsType: ArticleFieldsType.LESSON,
    defaultProjectId: projectId,
    data: selectedArticle,
    isEditing: !!selectedArticle,
    articleId: selectedArticle?.id || '',
    updateViewCount: !!selectedArticle?.id,
    updateArticleCallback: ({ updatedArticle }) => {
      dispatch({
        payload: {
          selectedArticle: updatedArticle,
        },
      });
      refetch();
    },
    onAddArticleCallback: ({ article }) => {
      dispatch({
        payload: {
          selectedArticle: article,
        },
      });
      refetch();
    },
  });
  const handleSubmit = useCallback(() => {
    if (isNovaAdmin) {
      setOpenNovaAdminConfirmation(true);
    } else {
      onSubmit();
    }
  }, [isNovaAdmin, onSubmit, setOpenNovaAdminConfirmation]);
  const {
    pluralRetrospectivesModuleName,
    projectModuleFeatures,
    singularRetrospectivesModuleName,
  } = useBuildWiseModules();
  const parentContainerRef = useRef<HTMLDivElement | null>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const { filters, isFiltersLoading, phasesWithCount, clearFilters, setFilters } =
    useRetrosFilters();
  const lessons = useMemo(() => {
    const phaseOnly: ArticleType[] = [];
    const noPhase: ArticleType[] = [];

    const filteredArticles = projectArticles.filter(
      (article) =>
        (!filters[0]?.selectedOptions ||
          filters[0]?.selectedOptions?.length === 0 ||
          article.projectPhases?.some((phase) => filters[0]?.selectedOptions?.includes(phase)) ||
          ((!article.projectPhases || article.projectPhases?.length === 0) &&
            filters[0]?.selectedOptions?.includes('(blank)'))) &&
        (!selectedLessonType?.id ||
          article.lessonTypes?.includes(selectedLessonType?.id) ||
          (!article.lessonTypes && selectedLessonType?.id === 'Blank')),
    );

    filteredArticles.forEach((article) => {
      if (article.projectPhases?.length) {
        phaseOnly.push(article);
      } else {
        noPhase.push(article);
      }
    });

    phaseOnly.sort((a, b) =>
      (a.projectPhases?.[0] || '').localeCompare(b.projectPhases?.[0] || ''),
    );

    // [BWA-1379] : returning learnings directly
    return filteredArticles;

    // return [...phaseOnly, ...noPhase];
  }, [phases, projectArticles, selectedLessonType, filters]);

  useEffect(() => {
    const updateHeight = () => {
      if (containerRef.current) {
        // Get the container's top position relative to the viewport
        const containerTop = containerRef.current.getBoundingClientRect().top;
        // Calculate height from its current position to the bottom of the viewport
        containerRef.current.style.height = `${window.innerHeight - containerTop}px`;
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight);
    };
  }, [containerRef.current, articleIds]);

  useEffect(() => {
    const updateHeight = () => {
      if (parentContainerRef.current) {
        if (isFullScreen) {
          parentContainerRef.current.style.maxHeight = 'fit-content';
        } else {
          const containerTop = parentContainerRef.current.getBoundingClientRect().top;
          parentContainerRef.current.style.height =
            view === 'TABLE' ? `${window.innerHeight - containerTop}px` : '100%';
          parentContainerRef.current.style.maxHeight =
            view === 'TABLE' ? `${window.innerHeight - 200}px` : '100%';
        }
      }
    };

    updateHeight();
    window.addEventListener('resize', updateHeight);
    window.addEventListener('scroll', updateHeight);

    return () => {
      window.removeEventListener('resize', updateHeight);
      window.removeEventListener('scroll', updateHeight);
    };
  }, [parentContainerRef.current, view, isFullScreen]);

  return (
    <FormProvider {...postForm}>
      <Box ref={parentContainerRef}>
        {!isFullScreen && (
          <Box
            sx={(theme) => ({
              position: 'sticky',
              top: 140,
              backgroundColor: theme.palette.custom.background,
              zIndex: 9,
              pb: 1,
            })}
          >
            <RetrosHeader filteredLessons={lessons} phasesWithCount={phasesWithCount} />
          </Box>
        )}
        {view === 'TABLE' ? (
          <RetrospectivesContainer
            sx={{
              ...(isFullScreen && {
                position: 'fixed',
                left: 0,
                top: 0,
                bottom: 0,
                right: 0,
                zIndex: 99,
                p: 0,
                overflow: 'auto',
              }),
            }}
          >
            {isFullScreen && (
              <>
                <StickyContainer>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    sx={(theme) => ({
                      backgroundColor: theme.palette.custom.white,
                      zIndex: 99,
                      px: 1,
                    })}
                  >
                    <Box display="flex" alignItems="center" p={2} gap={1} pb={1}>
                      <IconButton
                        onClick={() =>
                          dispatch(({ state }) => ({
                            payload: {
                              ...state,
                              isFullScreen: false,
                              view: 'CARD',
                            },
                          }))
                        }
                      >
                        <ArrowLeftLineIcon color={theme.palette.custom.navy} />
                      </IconButton>
                      <ProjectIconContainer>
                        <BuildingLineIcon color={theme.palette.custom.black} size={24} />
                      </ProjectIconContainer>
                      <Box ml={1}>
                        <Title>{project.name}</Title>
                        <Box
                          display="flex"
                          alignItems="center"
                          gap={1}
                          sx={(theme) => ({
                            '& svg path': { fill: theme.palette.custom.mistBlue },
                          })}
                        >
                          <RetrospectiveFillIcon width={20} />
                          <Title
                            sx={(theme) => ({ color: theme.palette.custom.mistBlue, fontSize: 16 })}
                          >
                            {pluralRetrospectivesModuleName}{' '}
                            {projectArticles.length ? `(${projectArticles.length})` : ''}
                          </Title>
                        </Box>
                      </Box>
                      <Button
                        variant="outlined"
                        startIcon={<AddLineIcon />}
                        onClick={() => {
                          postForm.reset({
                            ...getArticleFormDefaultValues({
                              defaultProjectId: projectId,
                              articleFields,
                            }),
                            projectPhases: '',
                            projectSpecific: true,
                          });
                          dispatch(({ state }) => ({
                            payload: {
                              ...state,
                              isOpenedLessonForm: true,
                              selectedArticle: undefined,
                              articleIds: [],
                              showSelectMilestone: false,
                            },
                          }));
                        }}
                        sx={(theme) => ({
                          backgroundColor: theme.palette.custom.white,
                          ml: 2,
                        })}
                      >
                        Add {singularRetrospectivesModuleName}
                      </Button>
                    </Box>
                    <RetrosHeader filteredLessons={lessons} phasesWithCount={phasesWithCount} />
                  </Box>
                  <Divider />
                </StickyContainer>
              </>
            )}

            <Box sx={{ display: 'flex', overflow: 'auto', gap: 1 }}>
              <PhasesDrawer phasesFilters={filters[0]} setFilters={setFilters} />
              <Box flex={1} py={1.5} sx={{ overflow: 'hidden' }}>
                <LessonsTable lessons={lessons} isLoading={isLoading} />
              </Box>
            </Box>
          </RetrospectivesContainer>
        ) : (
          <Grid container spacing={2}>
            <Grid
              item
              sm={12}
              md={
                projectModuleFeatures[BUILDWISE_MODULES_FEATURES.PROJECTS.ACTIVITY_LOG]?.isActive
                  ? 12
                  : 8
              }
            >
              {lessons?.map((article) => (
                <ArticleBox
                  key={article.id}
                  article={article}
                  variant="short"
                  doNotAutoplayVideos
                  project={project}
                  onArticleRedirect={() => {
                    dispatch(({ state }) => ({
                      payload: {
                        ...state,
                        selectedArticle: article,
                        articleIds: [],
                      },
                    }));
                  }}
                  onEditArticleCallback={() => {
                    postForm.reset({
                      ...getArticleFormDefaultValues({
                        article: article,
                        defaultProjectId: projectId,
                        articleFields,
                      }),
                      projectPhases: article.projectPhases?.[0] || '',
                    });
                    dispatch({
                      payload: {
                        isOpenedLessonForm: true,
                      },
                    });
                  }}
                />
              ))}
            </Grid>
            <Grid item md={4}>
              <Filter
                filters={filters}
                setFilters={setFilters}
                clearFilters={clearFilters}
                isLoading={isFiltersLoading}
                top={190}
              />
            </Grid>
          </Grid>
        )}
      </Box>

      {(isOpenedLessonForm || selectedArticle) && (
        <Box
          ref={containerRef}
          sx={(theme) => ({
            position: 'fixed',
            top: 0,
            bottom: 0,
            right: 0,
            width: 800,
            backgroundColor: theme.palette.custom.white,
            borderLeft: `1px solid ${theme.palette.custom.divider}`,
            boxShadow: `-6px 0 12px 0 ${theme.palette.custom.boxShadow}`,
            overflow: 'auto',
            zIndex: 99,
          })}
        >
          {!!articleIds.length ? (
            <RetroSummarizedResults />
          ) : isOpenedLessonForm ? (
            <AddLessonForm
              articleFields={articleFields}
              articleFieldsLoading={isLoadingArticleConfigFields}
              loadingFiles={loadingFiles}
              onDrop={onDrop}
              onSubmit={handleSubmit}
            />
          ) : (
            <ViewLearning />
          )}
        </Box>
      )}
      {openNovaAdminConfirmation && (
        <AlertModal
          open={openNovaAdminConfirmation}
          setOpen={setOpenNovaAdminConfirmation}
          handleCancel={() => {
            dispatch({ payload: { isOpenedLessonForm: false, showSelectMilestone: false } });
            setOpenNovaAdminConfirmation(false);
          }}
          onSubmit={onSubmit}
          loading={postForm.formState.isSubmitting}
          cancelLoading={discardLoading}
          title="Confirm"
          submitButtonName={'Yes'}
          cancelButtonName="No"
          description={'You are logged in as a BuildWise Admin. Are you sure you want to submit?'}
          discardActions
        />
      )}
    </FormProvider>
  );
}

export const RetrospectivesContainer = styled(Paper)(({ theme }) => ({
  boxShadow: `0 4px 24px ${theme.palette.custom.boxShadow}`,
  borderRadius: 8,
}));

const StickyContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.white,
  position: 'sticky',
  top: 0,
  zIndex: 9,
}));

const ProjectIconContainer = styled(Box)(({ theme }) => ({
  backgroundColor: theme.palette.custom.lightBlue,
  width: 48,
  height: 48,
  borderRadius: 5,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const Title = styled(Typography)(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.tuna,
}));

export default Retrospectives;
